import { firestore } from "../../config/ConfigFirebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

// PQRSD AI configuration: the dependency groups the classifier routes to, and
// the global classifier settings. Both are read server-side by
// gfunctions/pqrsd/_shared/pqrsdClassifier.js, so the field names here MUST match it.

const GROUPS = "pqrsdGroups";
const CONFIG = "config";
// Config is split across two docs in the shared `config` collection:
//   config/global — general app-wide settings (base URL + email brand) reused by
//                   every module (PQRSD, Extensión, …).
//   config/pqrsd  — PQRSD-specific settings (AI classifier + interim policy).
const GLOBAL_DOC = "global";
const PQRSD_DOC = "pqrsd";
// Keys that belong to config/global; everything else in DEFAULT_PQRSD_CONFIG is
// PQRSD-specific and lives in config/pqrsd.
const GENERAL_KEYS = ["orgName", "publicBaseUrl", "brandColor", "brandName", "logoUrl", "logoWidth"];
const pick = (obj, keys) => Object.fromEntries(keys.filter((k) => k in obj).map((k) => [k, obj[k]]));
const omit = (obj, keys) => Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));

// Default classifier settings — mirror DEFAULTS in pqrsdClassifier.js so the
// admin form shows the same values the server falls back to.
export const DEFAULT_PQRSD_CONFIG = {
  // Institution name, shown app-wide (postulación habeas-data, landing, emails).
  // General setting → stored in config/global. Blank = built-in default.
  orgName: "",
  aiEnabled: false,
  model: "gpt-4o-mini",
  systemPrompt: "",
  priorityCriteria: "",
  confidenceThreshold: 0.7,
  readAttachments: true,
  maxAttachmentBytes: 8 * 1024 * 1024,
  // Max interim ("informe de avance") responses allowed per cycle before the
  // next reply must be final. Cycle resets on each FINAL response. This is a
  // per-institution policy, so it lives here and is editable by any operator
  // with PQRSD.CONFIG. 0 = unlimited (no cap). Enforced server-side in
  // gfunctions/pqrsd/startPqrsdResponseFlow.
  maxInterimsPerCycle: 2,
  // --- Requerimiento de información al ciudadano (CPACA art. 17) ------------
  // Max requerimientos the entity may send per cycle. The norm contemplates a
  // single one; serial requerimientos to stretch the term are exactly what it
  // punishes, so the default is 1. 0 = unlimited (no cap). Enforced
  // server-side in gfunctions/pqrsd/handlePqrsd.
  maxInfoRequestsPerCycle: 1,
  // Calendar days the citizen gets to complete the petition. The norm says
  // "un (1) mes"; institutions may not shorten it below 30 in practice.
  citizenReplyDays: 30,
  // Extra calendar days granted when the citizen asks for a prórroga before
  // the deadline expires ("hasta por un término igual").
  citizenExtensionDays: 30,
  // When true the requerimiento goes through the supervised draft/approval
  // flow (startPqrsdResponseFlow) instead of being filed directly. Off by
  // default: the entity only has 10 days from radicación to require.
  infoRequestRequiresApproval: false,
  // Documents the module files by itself (radicación, requerimiento,
  // desistimiento) and the answer templates operators pick from when starting
  // a response: membretes, the text of each, and the merged .docx the cloud
  // functions render from. Shape and rules in
  // src/components/pqrsd/config/documentTemplates.js. Written by the Documentos
  // tab on its own, never by the General "Guardar ajustes".
  documents: {
    letterheads: [],
    defaultLetterheadId: "",
    bodies: {},
    merged: {},
    answers: [],
  },
  // Citizen-facing base URL for email magic links / QR (e.g.
  // "https://archivo.tdea.edu.co"). Read server-side by _shared/citizenToken.js.
  // Blank = auto-derive https://<projectId>.web.app at runtime (zero config).
  publicBaseUrl: "",
  // Email branding, shared centrally so a new instance is styled from here
  // (no env redeploy, no template edits). Consumed by emailTemplates.setBrand.
  // Blank = fall back to the REACT_APP_THEME / EMAIL_FROM_NAME env chain, then
  // the built-in default. brandColor must be a #rrggbb hex.
  brandColor: "",
  brandName: "",
  // Optional external logo (absolute https URL) shown in the email header, and
  // its rendered width in px (set once in the admin preview, carries across
  // every mail). Blank logoUrl = header shows the brand name only.
  logoUrl: "",
  logoWidth: 140,
};

// --- Config (split: general in config/global, PQRSD-specific in config/pqrsd) ---

// Merges both docs into the single flat shape the admin form expects. General
// fields (base URL, brand) come from config/global; the rest from config/pqrsd.
export const getPqrsdConfig = async () => {
  try {
    const [gSnap, pSnap] = await Promise.all([
      getDoc(doc(firestore, CONFIG, GLOBAL_DOC)),
      getDoc(doc(firestore, CONFIG, PQRSD_DOC)),
    ]);
    const general = gSnap.exists() ? pick(gSnap.data(), GENERAL_KEYS) : {};
    const pqrsd = pSnap.exists() ? omit(pSnap.data(), GENERAL_KEYS) : {};
    return { ...DEFAULT_PQRSD_CONFIG, ...pqrsd, ...general };
  } catch (e) {
    console.error("getPqrsdConfig error:", e);
    return { ...DEFAULT_PQRSD_CONFIG };
  }
};

// Splits an incoming patch: general keys → config/global (shared), the rest →
// config/pqrsd. Only writes a doc when that side of the patch is non-empty.
export const savePqrsdConfig = async (patch) => {
  try {
    const general = pick(patch, GENERAL_KEYS);
    const pqrsd = omit(patch, GENERAL_KEYS);
    const writes = [];
    if (Object.keys(general).length) {
      writes.push(setDoc(doc(firestore, CONFIG, GLOBAL_DOC), { ...general, updatedAt: serverTimestamp() }, { merge: true }));
    }
    if (Object.keys(pqrsd).length) {
      writes.push(setDoc(doc(firestore, CONFIG, PQRSD_DOC), { ...pqrsd, updatedAt: serverTimestamp() }, { merge: true }));
    }
    await Promise.all(writes);
    return true;
  } catch (e) {
    console.error("savePqrsdConfig error:", e);
    return false;
  }
};

// --- Dependency groups ------------------------------------------------------

// Normalizes the member picker output into the { userId, name, email } shape the
// classifier and the group-task notifier expect.
const normalizeMembers = (members) => {
  const arr = Array.isArray(members) ? members : [];
  const seen = new Set();
  const out = [];
  for (const m of arr) {
    if (!m?.userId || seen.has(m.userId)) continue;
    seen.add(m.userId);
    out.push({
      userId: m.userId,
      name: m.name || `${m.firstName || ""} ${m.lastName || ""}`.trim() || null,
      email: m.email || null,
    });
  }
  return out;
};

const mapGroup = (snap) => ({ id: snap.id, ...snap.data() });

export const listPqrsdGroups = async () => {
  try {
    const snap = await getDocs(query(collection(firestore, GROUPS), orderBy("name")));
    return snap.docs.map(mapGroup);
  } catch (e) {
    console.error("listPqrsdGroups error:", e);
    return [];
  }
};

export const subscribePqrsdGroups = (cb) => {
  const q = query(collection(firestore, GROUPS), orderBy("name"));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map(mapGroup)),
    (e) => {
      console.error("subscribePqrsdGroups error:", e);
      cb([]);
    }
  );
};

// Keep only supervisor ids that are actual members of the group — a lead must
// belong to the dependency they supervise.
const normalizeSupervisorIds = (supervisorIds, memberIds) => {
  const allowed = new Set(memberIds || []);
  return Array.from(new Set((supervisorIds || []).filter((id) => allowed.has(id))));
};

export const createPqrsdGroup = async ({ name, description, members, supervisorIds, active = true }) => {
  const list = normalizeMembers(members);
  const memberIds = list.map((m) => m.userId);
  const ref = await addDoc(collection(firestore, GROUPS), {
    name: (name || "").trim(),
    description: (description || "").trim(),
    members: list,
    memberIds,
    // Designated dependency leads (subset of memberIds). Empty = no lead yet.
    supervisorIds: normalizeSupervisorIds(supervisorIds, memberIds),
    active: !!active,
    slaOverrideDays: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

export const updatePqrsdGroup = async (id, { name, description, members, supervisorIds, active }) => {
  const patch = { updatedAt: serverTimestamp() };
  if (name !== undefined) patch.name = (name || "").trim();
  if (description !== undefined) patch.description = (description || "").trim();
  if (active !== undefined) patch.active = !!active;
  let nextMemberIds;
  if (members !== undefined) {
    const list = normalizeMembers(members);
    patch.members = list;
    patch.memberIds = list.map((m) => m.userId);
    nextMemberIds = patch.memberIds;
  }
  if (supervisorIds !== undefined) {
    // Reconcile against the incoming members when present, else trust the caller
    // (the editor always sends both together).
    patch.supervisorIds = normalizeSupervisorIds(supervisorIds, nextMemberIds ?? supervisorIds);
  }
  await updateDoc(doc(firestore, GROUPS, id), patch);
  return true;
};

export const deletePqrsdGroup = async (id) => {
  await deleteDoc(doc(firestore, GROUPS, id));
  return true;
};
